import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { I18nInfo } from '../models/i18n.model';
import { KeycloakService } from './keycloak.service';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private i18nUrl = '/i18n';
  public i18nInfo: I18nInfo;

  constructor(private http: HttpClient) {}

  loadI18nInfo(): Promise<I18nInfo> {
    console.log('Loading i18n info');
    return this.fetchI18nInfo()
      .toPromise()
      .then((data: any) => {
        this.i18nInfo = data;
        KeycloakService.i18nAcceptLanguageHeader = data.languageGuiCode;
        return data;
      })
      .catch((err: any) => {
        // Don't swallow silently: a failed i18n load leaves the language
        // switcher hidden, so surface the cause for diagnosis.
        console.error('Failed to load i18n info', err);
        return Promise.resolve(null as any);
      });
  }

  fetchI18nInfo(): Observable<I18nInfo> {
    return this.http.get<any>(this.i18nUrl + '/me').pipe(
      map((res) => {
        const data: I18nInfo = res.data;
        this.extractI18n(data.country);
        this.extractI18n(data.languageGui);
        this.extractI18n(data.languagePdf);
        this.deriveCodes(data);
        return data;
      }),
    );
  }

  private extractI18n(entity: any): any {
    // The backend may return an entity without translations (empty/absent i18n
    // array) for some org/locale combinations. Guard against it — like
    // lists.service does — so one missing label can't abort the whole i18n load
    // and leave i18nInfo undefined (which would hide the language switcher).
    if (entity && entity.i18n && entity.i18n.length > 0) {
      entity.label = entity.i18n[0].label;
    } else if (entity) {
      entity.label = entity.code || '';
    }
    return entity;
  }

  private deriveCodes(i18nInfo: I18nInfo) {
    const languageGuiCodeParts = i18nInfo.languageGuiCode.split('-');
    i18nInfo.languageGuiCode_lang_code = languageGuiCodeParts[0];
    i18nInfo.languageGuiCode_country_code = languageGuiCodeParts[1];

    const languagePdfCodeParts = i18nInfo.languagePdfCode.split('-');
    i18nInfo.languagePdfCode_lang_code = languagePdfCodeParts[0];
    i18nInfo.languagePdfCode_country_code = languagePdfCodeParts[1];
  }

  save(i18nInfo: I18nInfo): Observable<I18nInfo> {
    return this.http.put<any>(this.i18nUrl + '/me', i18nInfo).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private handleError(error: any) {
    console.error('An error occurred', error);
    return throwError(() => error.message || error);
  }
}
